import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { PRIORITIES, ITEM_STATUSES } from "@/lib/handover";
import { apiPatchItem, apiDeleteItem, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Hash, AlertTriangle } from "lucide-react";

// Backend requires a non-empty title for each item (used in PDF/email/calendar).
// Since the title input has been removed per user request, we derive it from
// the first line / first 60 chars of `details`, falling back to "(untitled)"
// so submits + carry-forward keep working end-to-end.
function deriveTitle(details) {
    const firstLine = (details || "").split("\n")[0].trim();
    const clipped = firstLine.slice(0, 60);
    return clipped || "(untitled)";
}

export default function ItemCard({ item, handoverId, canEdit, onChanged, onRemoved }) {
    const [details, setDetails] = useState(item.details || "");
    const [reference, setReference] = useState(item.reference || "");
    const isIncident = item.category === "Open Incident";
    const isOpen = item.item_status === "Open";
    const needsAction = isIncident && isOpen && !details.trim();

    async function patch(field, value) {
        try {
            const updated = await apiPatchItem(handoverId, item.item_id, {
                [field]: value,
            });
            onChanged?.(updated);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function patchDetails(nextDetails) {
        // Auto-sync title from the first line of details on each save so the
        // list / read-only view / PDF / email keep a meaningful heading.
        const derivedTitle = deriveTitle(nextDetails);
        const changes = { details: nextDetails };
        if (derivedTitle !== item.title) changes.title = derivedTitle;
        try {
            const updated = await apiPatchItem(handoverId, item.item_id, changes);
            onChanged?.(updated);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function remove() {
        if (!window.confirm(`Remove item "${item.title}"?`)) return;
        try {
            await apiDeleteItem(handoverId, item.item_id);
            toast.success("Item removed");
            onRemoved?.(item.item_id);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    // Read-only render
    if (!canEdit) {
        return (
            <div
                data-testid={`item-${item.item_id}`}
                className={`rounded-md border p-3 ${
                    needsAction
                        ? "bg-red-50 border-red-200"
                        : "bg-white border-ntt-border"
                }`}
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        {item.details ? (
                            <div className="text-[13px] text-[#070F26] whitespace-pre-wrap break-words">
                                {item.details}
                            </div>
                        ) : (
                            <div className="text-[13px] font-medium text-ntt-muted italic">
                                {item.title || "(untitled)"}
                            </div>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            {item.reference && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-ntt-muted bg-[#F4F6F9] rounded px-2 py-0.5">
                                    <Hash className="h-3 w-3" />
                                    {item.reference}
                                </span>
                            )}
                            {isIncident && item.priority !== "Not applicable" && (
                                <span
                                    className={`text-[11px] font-medium rounded px-2 py-0.5 ${priorityClasses(item.priority)}`}
                                >
                                    {item.priority}
                                </span>
                            )}
                            <span
                                className={`text-[11px] font-medium rounded px-2 py-0.5 ${
                                    item.item_status === "Open"
                                        ? "bg-[#DBEAF6] text-[#0072BC]"
                                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                }`}
                            >
                                {item.item_status}
                            </span>
                            {item.carry_forward && (
                                <span className="text-[11px] font-medium rounded px-2 py-0.5 bg-[#FFC303]/25 text-[#8a5a00]">
                                    carry forward
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid={`item-${item.item_id}`}
            className={`rounded-md border p-3 space-y-2 ${
                needsAction
                    ? "bg-red-50 border-red-200"
                    : "bg-white border-ntt-border"
            }`}
        >
            <div className="flex items-start gap-2">
                <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    onBlur={() =>
                        (details || "") !== (item.details || "") &&
                        patchDetails(details)
                    }
                    data-testid={`item-details-${item.item_id}`}
                    placeholder={
                        isIncident
                            ? "Description — next action, current owner, watch-outs…"
                            : "Description — details, links, watch-outs…"
                    }
                    className={`flex-1 min-h-[80px] rounded-md border p-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0072BC]/40 ${
                        needsAction ? "border-red-300" : "border-ntt-border"
                    }`}
                />
                <Button
                    variant="outline"
                    size="sm"
                    onClick={remove}
                    data-testid={`item-delete-${item.item_id}`}
                    className="h-9 text-red-600 hover:text-red-700 shrink-0"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            </div>
            {needsAction && (
                <div
                    data-testid={`item-need-action-${item.item_id}`}
                    className="flex items-center gap-1.5 text-[11px] text-red-700"
                >
                    <AlertTriangle className="h-3 w-3" />
                    Add a description to allow submit.
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-[minmax(140px,1fr)_minmax(120px,140px)_minmax(120px,140px)_auto] gap-2">
                <Input
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    onBlur={() =>
                        (reference || "") !== (item.reference || "") &&
                        patch("reference", reference)
                    }
                    data-testid={`item-ref-${item.item_id}`}
                    placeholder="Ticket / reference"
                    className="h-9"
                />
                {isIncident ? (
                    <Select
                        value={item.priority}
                        onValueChange={(v) => patch("priority", v)}
                    >
                        <SelectTrigger className="h-9" data-testid={`item-priority-${item.item_id}`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {PRIORITIES.map((p) => (
                                <SelectItem key={p} value={p}>
                                    {p}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <div className="text-[11px] text-ntt-muted h-9 flex items-center px-2">
                        Priority not applicable
                    </div>
                )}
                <Select
                    value={item.item_status}
                    onValueChange={(v) => patch("item_status", v)}
                >
                    <SelectTrigger className="h-9" data-testid={`item-status-${item.item_id}`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {ITEM_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                                {s}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <label
                    className="flex items-center gap-2 text-[12px] text-[#2B2F3A] border border-ntt-border rounded-md px-3 h-9 bg-white"
                >
                    <Switch
                        checked={!!item.carry_forward}
                        onCheckedChange={(v) => patch("carry_forward", v)}
                        data-testid={`item-cf-${item.item_id}`}
                    />
                    carry forward
                </label>
            </div>
        </div>
    );
}

function priorityClasses(p) {
    switch (p) {
        case "P1":
            return "bg-red-50 text-red-700 border border-red-200";
        case "P2":
            return "bg-orange-50 text-orange-700 border border-orange-200";
        case "P3":
            return "bg-yellow-50 text-yellow-800 border border-yellow-200";
        case "P4":
            return "bg-emerald-50 text-emerald-700 border border-emerald-200";
        default:
            return "bg-[#F4F6F9] text-ntt-muted border border-ntt-border";
    }
}
